/**
 * Helper function for simple property conversions
 * Creates toClass and fromClass functions for properties with pattern: prefix-value
 * @param {string} prefix - The CSS class prefix (e.g., 'float', 'text', 'flex')
 * @param {Array} excludeValues - Optional array of values to exclude from fromClass (e.g., ['0', '1', '2'])
 * @returns {Object} Object with toClass and fromClass functions
 */
export const createSimpleProperty = (prefix, excludeValues = []) => ({
    toClass: (value) => {
        if (value === undefined || value === null || value === '') return null;
        // If value looks like an arbitrary value (has units), wrap in brackets
        if (typeof value === 'string' && (value.includes('px') || value.includes('rem') || value.includes('%') || value.includes('vh') || value.includes('vw') || value.includes('calc'))) {
             return `${prefix}-[${value}]`;
        }
        return `${prefix}-${value}`;
    },
    fromClass: (className) => {
        // Handle bracket syntax
        const bracketMatch = className.match(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-\\[(.+)\\]$`));
        if (bracketMatch) {
            const val = bracketMatch[1];
            // If it looks like a color, reject it (let createColorProperty handle it)
            if (val.startsWith('#') || val.startsWith('rgb') || val.startsWith('hsl')) return null;
            return val;
        }

        const match = className.match(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(.+)$`));
        if (match) {
            const extractedValue = match[1];
            // Reject bracket syntax (arbitrary values) - these should be handled by bracketMatch
            if (extractedValue.startsWith('[') && extractedValue.endsWith(']')) return null;
            // Exclude specified values (e.g., to avoid conflicts)
            if (excludeValues.includes(extractedValue)) return null;
            return extractedValue;
        }
        return null;
    }
});

/**
 * Helper function for color properties
 * Creates toClass and fromClass functions for color properties
 * Same as createSimpleProperty but includes hex/rgb/hsl colors
 * @param {string} prefix - The CSS class prefix (e.g., 'bg', 'text', 'border')
 * @param {Array} excludeValues - Optional array of values to exclude from fromClass
 * @returns {Object} Object with toClass and fromClass functions
 */
export const createColorProperty = (prefix, excludeValues = []) => ({
    toClass: (value) => {
        if (!value) return null;
        // Use Tailwind's arbitrary value syntax for hex/rgb/hsl colors
        if (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl')) {
            return `${prefix}-[${value}]`;
        }
        // Standard color names don't need brackets
        return `${prefix}-${value}`;
    },
    fromClass: (className) => {
        // Match both bracket syntax (arbitrary values) and standard syntax
        const bracketMatch = className.match(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-\\[(.+)\\]$`));
        if (bracketMatch) {
            const colorValue = bracketMatch[1];
            return colorValue;
        }

        const standardMatch = className.match(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(.+)$`));
        if (standardMatch) {
            const colorValue = standardMatch[1];
            // Exclude non-color values (e.g., width, style values)
            if (excludeValues.includes(colorValue)) return null;
            return colorValue;
        }
        return null;
    }
});
